/**
 * Diurnal QC: settings, per-reading flags and events (phase 2b.3 D1, D2; spec docs/specs/diurnal-qc.md §3–§5;
 * Diurnal inventory 02 §3.1–§3.2, §2.2).
 *
 * - Settings: tenant defaults, the project's contract limit (diurnal chord, decision 13), project overrides, then the
 *   person's own screen values (decision 46). One default set for files and TA Live; no per-format defaults.
 * - Flags (the Diurnal tool's `calculateExceedanceFlags`, one rule set): Plot 1 always counts; Plot 2, Gradient, Pcal
 *   and DoR count only while their chart is shown (decision 47, Q20). Flags are raised only inside the edge trim
 *   (decision 24); the metrics use every stored reading. Gap: an interval longer than 1.5 × the sample period, on the
 *   reading before it, the same rule for flags, markers and alerts. Bad data: a reading the instrument marked
 *   ("?" on an 857, decision 22), a TA Live row with Good = false, or a spike or dropout Teslyn finds itself (R109,
 *   `findSpikes`): those are also left out of the QC windows, so one garbage reading is not an exceedance.
 */
import {
  BASEMAG_RULES,
  decimateMinMax,
  edgeTrimRange,
  FLAG_QUESTIONABLE,
  type BasemagFormat,
  type TrimRange,
} from './basemag.js';
import { qcRule, type QcRule } from './qc-spec.js';
import {
  DEFAULT_QC,
  DEFAULT_VISIBILITY,
  DIURNAL_CHARTS,
  isPlotMode,
  QC_KEYS,
  type DiurnalUserPrefs,
  type DiurnalVisibility,
  type PlotMode,
  type ProjectOverrides,
  type QcKey,
  type QcSettings,
} from './settings.js';
import { gradientMetric, pcalMetric, windowMetric } from './diurnal-metrics.js';
import { findSpikes } from './basemag-spikes.js';

/** A TA Live row with Good = false (a reading flag bit beside the import flags in basemag.ts). */
export const FLAG_BAD = 8;
/** Flag bits that make a reading "bad data". */
export const BAD_DATA_FLAGS = FLAG_QUESTIONABLE | FLAG_BAD;

/** A gap is an interval longer than this many sample periods (AC-3.3, one factor for flags and chart). */
export const GAP_FACTOR = 1.5;
/** Gaps longer than this are "high" severity alerts (the Diurnal tool's 300 s). */
export const GAP_HIGH_MS = 300_000;

export type DiurnalMetric = 'plot1' | 'plot2' | 'gradient' | 'pcal' | 'dor';

export const METRIC_BIT: Readonly<Record<DiurnalMetric, number>> = {
  plot1: 1,
  plot2: 2,
  gradient: 4,
  pcal: 8,
  dor: 16,
};

/** Labels as the Diurnal tool writes them in reasons and in the workbook's "Exceedance Source". */
export const METRIC_LABEL: Readonly<Record<DiurnalMetric, string>> = {
  plot1: 'Plot 1',
  plot2: 'Plot 2',
  gradient: 'Gradient',
  pcal: 'Pcal',
  dor: 'DoR',
};

/** Reason prefix per metric (the tool writes "StdDev" in reasons and "DoR" in the workbook). */
const REASON_LABEL: Readonly<Record<DiurnalMetric, string>> = { ...METRIC_LABEL, dor: 'StdDev' };

export const METRICS: readonly DiurnalMetric[] = ['plot1', 'plot2', 'gradient', 'pcal', 'dor'];

// ---------------------------------------------------------------------------------------------- settings

export interface DiurnalSettings extends QcSettings {
  readonly plotMode: PlotMode;
  readonly show: DiurnalVisibility;
}

export type DiurnalSettingSource = 'tenant' | 'contract' | 'project' | 'user' | 'format';

/** The contract's diurnal chord limit ("more than 10 nT from a 10-minute chord") as Plot 1 values. */
export function diurnalLimitsFromContract(rules: readonly QcRule[] | null | undefined): ProjectOverrides {
  const r = rules ? qcRule(rules, 'diurnal_chord') : null;
  if (!r || !r.enabled) return {};
  return {
    plot1ThresholdNt: r.limit,
    ...(r.window_min !== null && r.window_min > 0 ? { plot1WindowMin: r.window_min } : {}),
  };
}

export interface ResolvedDiurnal {
  readonly settings: DiurnalSettings;
  readonly source: Readonly<Record<QcKey | 'plotMode', DiurnalSettingSource>>;
  /** Where each chart's visibility came from: the tenant default, the project's chart defaults or the person. */
  readonly showSource: Readonly<Record<keyof DiurnalVisibility, DiurnalSettingSource>>;
  /** The values before the person's own changes (what "Reset to project values" restores). */
  readonly projectSettings: DiurnalSettings;
}

/**
 * Effective diurnal QC settings, later layers winning: tenant defaults, the contract's chord limit, project
 * overrides, the person's own values. The UTC offset and the online threshold are never personal.
 */
export function resolveDiurnalSettings(input: {
  readonly tenant?: QcSettings;
  readonly contract?: readonly QcRule[] | null;
  readonly project?: ProjectOverrides | null;
  readonly user?: DiurnalUserPrefs | null;
  readonly formats?: readonly BasemagFormat[];
}): ResolvedDiurnal {
  const tenant = input.tenant ?? DEFAULT_QC;
  const contract = diurnalLimitsFromContract(input.contract);
  const values: Record<string, number> = {};
  const source: Record<string, DiurnalSettingSource> = {};
  const projectValues: Record<string, number> = {};
  for (const k of QC_KEYS) {
    let v = tenant[k];
    let src: DiurnalSettingSource = 'tenant';
    const c = contract[k];
    if (c !== undefined) {
      v = c;
      src = 'contract';
    }
    const p = input.project?.[k];
    if (p !== undefined && p !== null && Number.isFinite(p)) {
      v = p;
      src = 'project';
    }
    projectValues[k] = v;
    const u = k === 'utcOffsetH' || k === 'onlineThresholdMin' ? undefined : input.user?.[k];
    if (u !== undefined && u !== null && Number.isFinite(u) && u >= 0) {
      v = u;
      src = 'user';
    }
    values[k] = v;
    source[k] = src;
  }
  // Plot mode (decision 248): tenant → format (823/862 start in peak to peak, as the tool) → contract → project →
  // the person's own choice for this project
  const base = projectPlotMode(input.formats ?? [], input.contract, input.project);
  const userMode = input.user?.plotMode;
  const plotMode: PlotMode = userMode ?? base.mode;
  source.plotMode = userMode ? 'user' : base.source;
  // Charts shown (decision 351): tenant default (all hidden, every format) → the project's chart defaults → the
  // person's own toggles for this project
  const show: Record<string, boolean> = { ...DEFAULT_VISIBILITY };
  const projectShow: Record<string, boolean> = { ...DEFAULT_VISIBILITY };
  const showSource: Record<string, DiurnalSettingSource> = {};
  for (const { key } of DIURNAL_CHARTS) {
    showSource[key] = 'tenant';
    const p = input.project?.show?.[key];
    if (typeof p === 'boolean') {
      show[key] = p;
      projectShow[key] = p;
      showSource[key] = 'project';
    }
    const u = input.user?.show?.[key];
    if (typeof u === 'boolean') {
      show[key] = u;
      showSource[key] = 'user';
    }
  }
  const qc = values as unknown as QcSettings;
  return {
    settings: { ...qc, plotMode, show: show as unknown as DiurnalVisibility },
    source: source as ResolvedDiurnal['source'],
    showSource: showSource as unknown as ResolvedDiurnal['showSource'],
    projectSettings: {
      ...(projectValues as unknown as QcSettings),
      plotMode: base.mode,
      show: projectShow as unknown as DiurnalVisibility,
    },
  };
}

/** Formats the Diurnal tool always opens in peak to peak (`processing.js:641-665`: 823 and 862). */
const PEAK_TO_PEAK_FORMATS: ReadonlySet<BasemagFormat> = new Set(['g823_dat', 'g862_txt']);

/**
 * The contract's mode: deviation from chord when it holds the chord rule alone. A contract with the non-linear rule
 * too (Benz F26048) names no single mode, so the format decides.
 */
export function contractPlotMode(rules: readonly QcRule[] | null | undefined): PlotMode | null {
  if (!rules) return null;
  const chord = qcRule(rules, 'diurnal_chord');
  const nonlinear = qcRule(rules, 'diurnal_nonlinear');
  if (chord?.enabled && !nonlinear?.enabled) return 'deviationFromChord';
  return null;
}

/** The project's Diurnal QC mode before the person's own choice, with where it came from. */
export function projectPlotMode(
  formats: readonly BasemagFormat[],
  contract: readonly QcRule[] | null | undefined,
  project: ProjectOverrides | null | undefined,
): { mode: PlotMode; source: DiurnalSettingSource } {
  if (isPlotMode(project?.plotMode)) return { mode: project.plotMode, source: 'project' };
  const c = contractPlotMode(contract);
  if (c) return { mode: c, source: 'contract' };
  if (formats.some((f) => PEAK_TO_PEAK_FORMATS.has(f))) return { mode: 'peakToPeak', source: 'format' };
  return { mode: 'deviationFromChord', source: 'tenant' };
}

// ---------------------------------------------------------------------------------------------- flags

/** Median positive interval, ms (the sample period); null with fewer than two distinct times. */
export function samplePeriodMs(tMs: ArrayLike<number>): number | null {
  const d: number[] = [];
  for (let i = 1; i < tMs.length; i++) {
    const x = tMs[i]! - tMs[i - 1]!;
    if (x > 0) d.push(x);
  }
  if (d.length === 0) return null;
  d.sort((a, b) => a - b);
  const m = d.length >> 1;
  return d.length % 2 === 1 ? d[m]! : (d[m - 1]! + d[m]!) / 2;
}

export interface DiurnalSeriesInput {
  /** UTC epoch ms, ascending. */
  readonly tMs: ArrayLike<number>;
  readonly values: ArrayLike<number>;
  /** Reading flags (basemag.ts bits, plus FLAG_BAD). */
  readonly flags?: ArrayLike<number> | null;
}

export interface DiurnalQc {
  readonly n: number;
  readonly settings: DiurnalSettings;
  readonly tMs: ArrayLike<number>;
  readonly values: ArrayLike<number>;
  /** Plot 1 / Plot 2 in the settings' mode; NaN where the window gives no value. */
  readonly plot1: Float64Array;
  readonly plot2: Float64Array;
  /** Computed only while shown (a hidden chart is neither drawn nor flagged); otherwise null. */
  readonly gradient: Float64Array | null;
  readonly pcal: Float64Array | null;
  readonly dor: Float64Array | null;
  /** Per reading: bits of METRIC_BIT that exceed their threshold. */
  readonly exceed: Uint8Array;
  /** Per reading: 1 when the interval after it is a gap. */
  readonly gapAfter: Uint8Array;
  /** Per reading: 1 when it is bad data. */
  readonly bad: Uint8Array;
  /** Per reading: 1 when Teslyn found it a spike or dropout (R109); such a reading has no metric values. */
  readonly spike: Uint8Array;
  /** Readings inside the edge trim (charts and flags); the rest are hidden. */
  readonly trim: TrimRange;
  readonly periodMs: number | null;
}

const threshold = (s: DiurnalSettings, m: DiurnalMetric): number =>
  m === 'plot1'
    ? s.plot1ThresholdNt
    : m === 'plot2'
      ? s.plot2ThresholdNt
      : m === 'gradient'
        ? s.gradientThresholdNt
        : m === 'pcal'
          ? s.pcalThresholdNt
          : s.dorThresholdNt;

export function metricThreshold(s: DiurnalSettings, m: DiurnalMetric): number {
  return threshold(s, m);
}

/** Whether a metric raises flags under these settings (Plot 1 always). */
export function metricCounts(s: DiurnalSettings, m: DiurnalMetric): boolean {
  return m === 'plot1' || s.show[m];
}

/** The metrics and flags of one series. Pure; the same input and settings always give the same result. */
/**
 * The metrics over the readings that are not spikes (R109), placed back at their own readings; a spike gets NaN.
 * With no spike, the metric over every reading as before.
 */
function withoutSpikes(
  tMs: ArrayLike<number>,
  values: ArrayLike<number>,
  spike: Uint8Array,
  metric: (t: ArrayLike<number>, v: ArrayLike<number>) => Float64Array,
): Float64Array {
  const keep: number[] = [];
  for (let i = 0; i < values.length; i++) if (!spike[i]) keep.push(i);
  if (keep.length === values.length) return metric(tMs, values);
  const sub = metric(
    keep.map((i) => tMs[i]!),
    keep.map((i) => values[i]!),
  );
  const out = new Float64Array(values.length).fill(NaN);
  keep.forEach((i, k) => (out[i] = sub[k]!));
  return out;
}

export function computeDiurnalQc(input: DiurnalSeriesInput, settings: DiurnalSettings): DiurnalQc {
  const { tMs, values } = input;
  const n = values.length;
  const mode = settings.plotMode;
  const spike = findSpikes(values);
  const m = (f: (t: ArrayLike<number>, v: ArrayLike<number>) => Float64Array): Float64Array =>
    withoutSpikes(tMs, values, spike, f);
  const plot1 = m((t, v) => windowMetric(t, v, settings.plot1WindowMin, mode));
  const plot2 = m((t, v) => windowMetric(t, v, settings.plot2WindowMin, mode));
  const gradient = settings.show.gradient
    ? m((t, v) => gradientMetric(t, v, settings.gradientWindowMin))
    : null;
  const pcal = settings.show.pcal ? m((_t, v) => pcalMetric(v)) : null;
  const dor = settings.show.dor ? m((t, v) => windowMetric(t, v, settings.dorWindowMin, 'stdDev')) : null;
  const trim = edgeTrimRange(tMs, settings.edgeTrimMin);
  const exceed = new Uint8Array(n);
  const gapAfter = new Uint8Array(n);
  const bad = new Uint8Array(n);
  const periodMs = samplePeriodMs(tMs);
  const arrays: ReadonlyArray<[DiurnalMetric, Float64Array | null, boolean]> = [
    ['plot1', plot1, true],
    ['plot2', plot2, settings.show.plot2],
    ['gradient', gradient, settings.show.gradient],
    ['pcal', pcal, settings.show.pcal],
    ['dor', dor, settings.show.dor],
  ];
  for (const [m, arr, counts] of arrays) {
    if (!arr || !counts) continue;
    const t = threshold(settings, m);
    const bit = METRIC_BIT[m];
    for (let i = trim.start; i < trim.end; i++) {
      const v = arr[i]!;
      if (!Number.isNaN(v) && Math.abs(v) > t) exceed[i]! |= bit;
    }
  }
  const flags = input.flags;
  for (let i = trim.start; i < trim.end; i++) {
    if ((flags && (flags[i]! & BAD_DATA_FLAGS) !== 0) || spike[i]) bad[i] = 1;
    if (periodMs !== null && i + 1 < n && tMs[i + 1]! - tMs[i]! > GAP_FACTOR * periodMs) gapAfter[i] = 1;
  }
  return {
    n,
    settings,
    tMs,
    values,
    plot1,
    plot2,
    gradient,
    pcal,
    dor,
    exceed,
    gapAfter,
    bad,
    spike,
    trim,
    periodMs,
  };
}

export function metricSeries(qc: DiurnalQc, m: DiurnalMetric): Float64Array | null {
  return qc[m];
}

/** JavaScript's own number text, as the Diurnal tool prints thresholds ("10", "2.5"). */
const jsNum = (x: number): string => String(x);

/** The reasons for one reading, in the Diurnal tool's words: `Plot 1 (12.3 > 10)`. Empty when not flagged. */
export function exceedanceReasons(qc: DiurnalQc, i: number): string[] {
  const out: string[] = [];
  const e = qc.exceed[i]!;
  for (const m of METRICS) {
    if ((e & METRIC_BIT[m]) === 0) continue;
    const v = qc[m]![i]!;
    out.push(`${REASON_LABEL[m]} (${Math.abs(v).toFixed(1)} > ${jsNum(threshold(qc.settings, m))})`);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------- events

export interface ExceedancePeak {
  readonly metric: DiurnalMetric;
  /** The signed metric value. */
  readonly value: number;
  readonly threshold: number;
  /** |value| − threshold, nT. */
  readonly excess: number;
  readonly at_ms: number;
}

/** Consecutive flagged readings (AC-2.1). */
export interface ExceedanceEvent {
  readonly start_index: number;
  readonly end_index: number;
  readonly start_ms: number;
  readonly end_ms: number;
  readonly readings: number;
  /** Metrics that flagged at least one reading of the event, in Plot 1, Plot 2, Gradient, Pcal, DoR order. */
  readonly metrics: readonly DiurnalMetric[];
  readonly peak: ExceedancePeak;
}

/**
 * Whether reading `i` is a spike (R109) with an exceeding reading after it inside the trim, past any further spikes:
 * a spike has no metric values, so it neither starts nor breaks a run of exceedance.
 */
function spikeInsideRun(qc: DiurnalQc, i: number): boolean {
  if (!qc.spike[i]) return false;
  let j = i + 1;
  while (j < qc.trim.end && qc.spike[j]) j++;
  return j < qc.trim.end && qc.exceed[j] !== 0;
}

export function exceedanceEvents(qc: DiurnalQc): ExceedanceEvent[] {
  const out: ExceedanceEvent[] = [];
  let i = qc.trim.start;
  while (i < qc.trim.end) {
    if (qc.exceed[i] === 0) {
      i++;
      continue;
    }
    const start = i;
    let bits = 0;
    let peak: ExceedancePeak | null = null;
    while (i < qc.trim.end && (qc.exceed[i] !== 0 || spikeInsideRun(qc, i))) {
      const e = qc.exceed[i]!;
      bits |= e;
      for (const m of METRICS) {
        if ((e & METRIC_BIT[m]) === 0) continue;
        const v = qc[m]![i]!;
        const t = threshold(qc.settings, m);
        const excess = Math.abs(v) - t;
        if (!peak || excess > peak.excess)
          peak = { metric: m, value: v, threshold: t, excess, at_ms: qc.tMs[i]! };
      }
      i++;
    }
    out.push({
      start_index: start,
      end_index: i - 1,
      start_ms: qc.tMs[start]!,
      end_ms: qc.tMs[i - 1]!,
      readings: i - start,
      metrics: METRICS.filter((m) => (bits & METRIC_BIT[m]) !== 0),
      peak: peak!,
    });
  }
  return out;
}

export interface GapEvent {
  /** The reading before the gap. */
  readonly index: number;
  readonly start_ms: number;
  readonly end_ms: number;
  readonly duration_ms: number;
  /** max(1, round(duration / period) − 1), as the Diurnal tool counts them. */
  readonly missing_readings: number;
  readonly severity: 'high' | 'medium';
  /** Longer than 10 sample periods (listed one by one; short gaps are summarised). */
  readonly large: boolean;
}

export function gapEvents(qc: DiurnalQc): GapEvent[] {
  const out: GapEvent[] = [];
  const p = qc.periodMs ?? 1000;
  for (let i = qc.trim.start; i < qc.trim.end; i++) {
    if (!qc.gapAfter[i]) continue;
    const start = qc.tMs[i]!;
    const end = qc.tMs[i + 1]!;
    const dur = end - start;
    out.push({
      index: i,
      start_ms: start,
      end_ms: end,
      duration_ms: dur,
      missing_readings: Math.max(1, Math.round(dur / p) - 1),
      severity: dur > GAP_HIGH_MS ? 'high' : 'medium',
      large: dur > 10 * p,
    });
  }
  return out;
}

export interface BadDataEvent {
  readonly start_index: number;
  readonly end_index: number;
  readonly start_ms: number;
  readonly end_ms: number;
  readonly readings: number;
  /** Of `readings`, how many Teslyn found spikes or dropouts (R109); the rest the instrument marked. */
  readonly spikes: number;
}

/** Consecutive bad readings grouped into one event (AC-2.2), keyed by the first reading's UTC time. */
export function badDataEvents(qc: DiurnalQc): BadDataEvent[] {
  const out: BadDataEvent[] = [];
  let i = qc.trim.start;
  while (i < qc.trim.end) {
    if (!qc.bad[i]) {
      i++;
      continue;
    }
    const s = i;
    let spikes = 0;
    while (i < qc.trim.end && qc.bad[i]) {
      if (qc.spike[i]) spikes++;
      i++;
    }
    out.push({
      start_index: s,
      end_index: i - 1,
      start_ms: qc.tMs[s]!,
      end_ms: qc.tMs[i - 1]!,
      readings: i - s,
      spikes,
    });
  }
  return out;
}

export interface DiurnalCounts {
  readonly exceedance_events: number;
  readonly exceeded_readings: number;
  readonly gaps: number;
  readonly bad_readings: number;
}

export function diurnalCounts(qc: DiurnalQc): DiurnalCounts {
  let exceeded = 0;
  let gaps = 0;
  let bad = 0;
  for (let i = 0; i < qc.n; i++) {
    if (qc.exceed[i]) exceeded++;
    if (qc.gapAfter[i]) gaps++;
    if (qc.bad[i]) bad++;
  }
  return {
    exceedance_events: exceedanceEvents(qc).length,
    exceeded_readings: exceeded,
    gaps,
    bad_readings: bad,
  };
}

/** The 4-dataset limit of the QC screen (decision 51, Q24). */
export const MAX_DIURNAL_DATASETS = 4;

/**
 * The readings a chart draws inside the edge trim: the deterministic min/max decimation of the values (I14), plus
 * every flagged reading (exceedance, bad data, both sides of a gap) up to `maxPoints` of them, else the first and
 * last of each flagged run. Every marker is therefore drawn at its reading's own time, whatever the decimation
 * (AC-3.4; the Diurnal tool read full-resolution flags with decimated indices). `of` is the series thinned: the
 * readings for the diurnal chart, a metric's own values for its chart, so a metric chart never loses its own peaks
 * between the diurnal's (MB's R56, 25 Oct 2026: Plot 1's highest value on the F26048 day 284 MagStation was drawn
 * 0.19 nT low).
 */
export function chartIndices(
  qc: DiurnalQc,
  maxPoints: number = BASEMAG_RULES.chartMaxPoints,
  of: ArrayLike<number> = qc.values,
): number[] {
  const { start, end } = qc.trim;
  const slice: number[] = [];
  for (let i = start; i < end; i++) slice.push(of[i]!);
  const keep = new Set<number>(decimateMinMax(slice, maxPoints).map((k) => k + start));
  const flagged: number[] = [];
  for (let i = start; i < end; i++) {
    if (qc.exceed[i] || qc.bad[i] || qc.gapAfter[i] || (i > start && qc.gapAfter[i - 1])) flagged.push(i);
  }
  if (flagged.length <= maxPoints) for (const i of flagged) keep.add(i);
  else {
    for (let k = 0; k < flagged.length; k++) {
      const i = flagged[k]!;
      if (k === 0 || flagged[k - 1] !== i - 1 || k === flagged.length - 1 || flagged[k + 1] !== i + 1)
        keep.add(i);
    }
  }
  return [...keep].sort((a, b) => a - b);
}
